// src/components/documents/DocumentCustomerCard.js — doced-8f3a61c2 (Document editor, Stage 4)
//
// The customer on a document page: a card (name, address, email · phone) with Change, or — with no
// customer yet — the existing type-to-filter customer search and "+ New customer". Below the card: the
// service location (required when the customer has properties) and Bill to, exactly as the estimate
// pop-up offers them. Every rule is utils/documentCustomer.js (the pop-up's handlers, proven by test).
//
// The customer can't be changed when the estimate was started FROM a customer (their page, a lead, a
// re-quote) — as in the pop-up, which locks the picker then.
// The email shown is the estimate's OWN copy — the address it is sent to — with "Change email" (Stage 5f-2).

import React, { useState } from 'react';
import CustomerCombobox from '../crm/CustomerCombobox';
import CustomerEmailChange from './CustomerEmailChange'; // doced-8f3a61c2 — Stage 5f-2
import { estimateEmailOf, differsFromRecord } from '../../utils/estimateEmailChange';
import { serviceLocationOptions, billToChoiceValue, cardAddress } from '../../utils/documentCustomer';
import { SELECT, SELECT_STYLE } from './fieldStyles';

export default function DocumentCustomerCard({
    quoteData, customers, locked, serviceLocation, pendingThirdParty,
    onChooseCustomer, onServiceLocation, onBillTo, onThirdParty, onNewCustomer, onClear, showErrors,
    canUpdateRecord, onChangeEmail, // doced-8f3a61c2 — Stage 5f-2
}) {
    const customer = (customers || []).find((c) => c.id === quoteData.customerId) || null;
    const hasCustomer = !!quoteData.customerId;
    const details = quoteData.customerDetails || {};
    const name = quoteData.customerName || details.name || customer?.name || 'Unnamed customer';
    // doced-8f3a61c2 — Stage 5f-2: the email shown is the ESTIMATE's own (what it is sent to) — never the
    // customer record's in its place — with a flag when the two differ.
    const email = estimateEmailOf(quoteData);
    const differs = differsFromRecord(email, customer);
    const phone = details.phone || customer?.phone;
    const [changingEmail, setChangingEmail] = useState(false);
    const [emailNotice, setEmailNotice] = useState('');
    // One click: the customer record takes the estimate's address (only the record is written).
    const [matching, setMatching] = useState(false);
    const updateRecordToMatch = async () => {
        setMatching(true); setEmailNotice('');
        try {
            const r = await onChangeEmail(email, true, customer?.email);
            setEmailNotice((r && r.message) || '');
        } finally { setMatching(false); }
    };
    const address = cardAddress(quoteData, customer);
    const locations = serviceLocationOptions(customer);
    const chosenLocation = locations.find((o) => o.value === serviceLocation) || null;

    return (
        <div className="flex flex-col gap-3">
            <span className="text-[13px] font-semibold text-gray-700" id="doced-customer-label">Customer</span>
            {hasCustomer ? (
                <div className="border border-gray-200 rounded-xl px-4 py-3 flex justify-between gap-3 bg-gray-50">
                    <div className="text-sm leading-relaxed min-w-0">
                        <div className="font-bold text-gray-900 truncate">{name}</div>
                        {address && <div className="text-gray-600">{address}</div>}
                        <div className="flex flex-wrap items-center gap-x-2 text-gray-600 break-words">
                            {email ? <span className="break-all">{email}</span> : <span className="text-amber-800">No email on this estimate</span>}
                            {phone && <span>· {phone}</span>}
                            {onChangeEmail && !changingEmail && (
                                <button type="button" onClick={() => { setChangingEmail(true); setEmailNotice(''); }}
                                    className="min-h-[44px] px-1 text-[#4f40b0] font-semibold hover:underline">{email ? 'Change email' : 'Add email'}</button>
                            )}
                        </div>
                        {differs && (
                            <div className="text-xs text-amber-800 mt-0.5">
                                Different from the customer record ({customer.email}). This estimate is emailed to the address above.
                                {canUpdateRecord && onChangeEmail && (
                                    <> <button type="button" disabled={matching} onClick={updateRecordToMatch}
                                        className="min-h-[44px] font-semibold text-[#4f40b0] hover:underline disabled:opacity-60">Update the customer record to match</button></>
                                )}
                            </div>
                        )}
                        {emailNotice && <div role="status" className="text-xs text-amber-800 mt-0.5">{emailNotice}</div>}
                        {!customer && <div className="text-xs text-amber-700 mt-1">This customer isn’t in your customer list any more.</div>}
                    </div>
                    {!locked && (
                        <button type="button" onClick={onClear} className="self-start min-h-[44px] px-3 rounded-lg text-[#4f40b0] font-semibold hover:bg-white">Change</button>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    <CustomerCombobox
                        name="customerId"
                        customers={customers || []}
                        value=""
                        onChange={(e) => onChooseCustomer(e.target.value)}
                        invalid={showErrors}
                        emptyLabel="Find a customer…"
                    />
                    <button type="button" onClick={onNewCustomer}
                        className="self-start min-h-[44px] px-3 rounded-lg text-[#4f40b0] font-bold hover:bg-[#5F4EC9]/10">+ New customer</button>
                </div>
            )}

            {hasCustomer && changingEmail && onChangeEmail && (
                <CustomerEmailChange
                    idPrefix="doced-customer"
                    current={email}
                    recordEmail={customer?.email}
                    customerName={name}
                    canUpdateRecord={canUpdateRecord}
                    hasRecord={!!customer}
                    onSave={async (value, alsoRecord) => {
                        const r = await onChangeEmail(value, alsoRecord, customer?.email);
                        if (r && r.ok === false) return r;
                        setChangingEmail(false);
                        setEmailNotice((r && r.message) || '');
                        return r;
                    }}
                    onCancel={() => setChangingEmail(false)}
                />
            )}

            {hasCustomer && locations.length > 0 && (
                <div className="flex flex-col gap-1">
                    <label htmlFor="doced-location" className="text-[13px] font-semibold text-gray-700">Service location <span className="text-red-600" aria-hidden="true">*</span></label>
                    <select id="doced-location" value={serviceLocation} onChange={(e) => onServiceLocation(e.target.value)}
                        title={chosenLocation ? chosenLocation.label : undefined} aria-describedby={chosenLocation ? 'doced-location-full' : undefined}
                        aria-invalid={serviceLocation === ''} style={SELECT_STYLE} className={`${SELECT} ${serviceLocation === '' ? '!border-red-400' : ''}`}>
                        <option value="">Choose a service location…</option>
                        {locations.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {/* The closed box can't show a long address in full, so it is written out underneath. */}
                    {chosenLocation && <span id="doced-location-full" className="text-xs text-gray-600 break-words">{chosenLocation.label}</span>}
                    {serviceLocation === '' && <span className="text-xs text-red-600">This customer has more than one location — choose which one.</span>}
                </div>
            )}

            {hasCustomer && (
                <div className="flex flex-col gap-1">
                    <label htmlFor="doced-billto" className="text-[13px] font-semibold text-gray-700">Bill to</label>
                    <select id="doced-billto" value={billToChoiceValue(quoteData, pendingThirdParty)} onChange={(e) => onBillTo(e.target.value)}
                        style={SELECT_STYLE} className={SELECT}>
                        <option value="main">Main customer</option>
                        {quoteData.propertyId ? <option value="propertyContact">Property contact</option> : null}
                        <option value="thirdParty">Another customer…</option>
                    </select>
                    {(pendingThirdParty || quoteData.billTo?.mode === 'thirdParty') && (
                        <CustomerCombobox
                            customers={(customers || []).filter((c) => c.id !== quoteData.customerId)}
                            value={quoteData.billTo?.customerId || ''}
                            onChange={(e) => onThirdParty(e.target.value)}
                            emptyLabel="Choose the customer to bill…"
                            invalid={!quoteData.billTo}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
